import { FeatureFlagKey } from "@redotech/feature-flags-model";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";

import { createContext, memo } from "react";

export interface SupportSuiteStatuses {
  supportEnabled: boolean;
  supportV2Enabled: boolean;
}

export const SupportSuiteStatusesContext = createContext<
  SupportSuiteStatuses | undefined
>(undefined);

let cachedSupportV2Enabled: boolean | null = null;
let supportV2FlagPromise: Promise<boolean> | null = null;

export const SupportSuiteStatusesProvider = memo(
  function SupportSuiteStatusesProvider({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const team = useRequiredContext(TeamContext);
    const platformRpcClient = useRequiredContext(
      RedoPlatformAuthRpcClientContext,
    );
    const settings = team.settings;

    const supportEnabled = !!settings.support?.enabled;

    const supportV2Load = useLoad(async () => {
      if (cachedSupportV2Enabled !== null) {
        return cachedSupportV2Enabled;
      }

      if (supportV2FlagPromise === null) {
        supportV2FlagPromise = platformRpcClient
          .getFeatureFlag({ flagKey: FeatureFlagKey.SUPPORT_HELPDESK_V2 })
          .then((response) => {
            cachedSupportV2Enabled = response.enabled;
            return response.enabled;
          });
      }

      return supportV2FlagPromise;
    }, []);

    if (supportV2Load.pending) {
      return null;
    }

    const supportV2Enabled = supportV2Load.value ?? false;

    const supportSuiteStatuses = { supportEnabled, supportV2Enabled };

    return (
      <SupportSuiteStatusesContext.Provider value={supportSuiteStatuses}>
        {children}
      </SupportSuiteStatusesContext.Provider>
    );
  },
);

export { default as SUPPORT_ICON } from "@redotech/redo-icons/message-text-square-02.svg";
export { default as SUPPORT_ICON_FILLED } from "@redotech/redo-icons/message-text-square-02_filled.svg";
